import { useEffect, useRef, useState } from "react";
import { transformLegacySiteConfig } from "@/components/organisms/Config/helpers";
import { useLazyGetAccountsQuery } from "@/redux/apis/account/accountApi";
import { useLazyVerifyProviderQuery } from "@/redux/apis/auth/authApi";
import { selectActiveOperator } from "@/redux/apis/operator/helpers";
import { useLazyGetClientOperatorsQuery } from "@/redux/apis/operator/operatorApi";
import { useLazyGetProfileQuery } from "@/redux/apis/profile/profileApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setActiveAccount } from "@/redux/slices/account/accountSlice";
import { setConfig } from "@/redux/slices/config/configSlice";
import { setActiveOperator } from "@/redux/slices/operator/operatorSlice";
import { siteInfoSelector } from "@/redux/slices/config/selectors";

/**
 * Lightweight account setup hook for client portal.
 * Fetches accounts and profile, then re-verifies the client's provider
 * to load the correct branding/config for their operator.
 * Stays in loading state until branding is resolved.
 */
export const useSetupClientAccount = () => {
  const dispatch = useAppDispatch();
  const activeAccount = useAppSelector((state) => state.account.activeAccount);
  const siteInfo = useAppSelector(siteInfoSelector);
  const [brandingReady, setBrandingReady] = useState(false);
  const hasRun = useRef(false);

  const [getAccounts, { isFetching: isFetchingAccounts }] = useLazyGetAccountsQuery();
  const [getProfile, { data: profile, isFetching: isFetchingProfile }] = useLazyGetProfileQuery();
  const [getClientOperators] = useLazyGetClientOperatorsQuery();
  const [verifyProvider] = useLazyVerifyProviderQuery();

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    getAccounts()
      .unwrap()
      .then((res) => {
        const accounts = res.data.accounts;
        const primaryAccount = accounts.find((account) => account.primary);
        if (!primaryAccount) {
          console.error("useSetupClientAccount: No primary account found");
          setBrandingReady(true);
          return;
        }
        dispatch(setActiveAccount(primaryAccount));
        // Brand from the client's ACTIVE provider, not profile.accountMetadata.providerCode — the
        // latter can lag behind after a detach/re-join (still points at the left operator). The
        // account-management providers list is the source of truth: pick the active relationship,
        // and only then resolve its full config via verifyProvider.
        Promise.all([getProfile().unwrap(), getClientOperators().unwrap()])
          .then(([, operatorsRes]) => {
            const providerCode = selectActiveOperator(operatorsRes.data.operators)?.providerCode;
            if (providerCode && siteInfo) {
              return verifyProvider({ providerCode, apiUrl: siteInfo.apiUrl })
                .unwrap()
                .then((operator) => {
                  // The same resolved operator the branding is built from is also the only fresh answer
                  // the portal gets about the ADD-ONS. Without this write, `activeOperator` stays at
                  // whatever redux-persist restored from the last login — a record that can predate the
                  // field entirely — and an unstated add-on is indistinguishable from an unresolved one,
                  // with no event that could ever clear it. `useFlightTrackingEnabled` depends on this
                  // to default OFF safely.
                  //
                  // Only the SUCCESS path, so a rejected verify (or a missing providerCode/siteInfo) still
                  // leaves a persisted record standing while `brandingReady` lets the sheet mount. That
                  // record is read as authoritative — which is today's behaviour, and no longer destroys
                  // anything now that an untouched edit re-commits its booked wait and landing unchanged.
                  dispatch(setActiveOperator(operator));
                  dispatch(setConfig(transformLegacySiteConfig({ siteInfo, operator })));
                })
                .catch(() => {
                  console.warn("useSetupClientAccount: Failed to verify client provider, using default config");
                });
            }
          })
          .catch(() => {
            console.warn("useSetupClientAccount: Failed to resolve client provider for branding");
          })
          .finally(() => setBrandingReady(true));
      })
      .catch(() => {
        console.error("useSetupClientAccount: Failed to fetch accounts");
        setBrandingReady(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFetching = isFetchingAccounts || isFetchingProfile;
  const isReady = !isFetching && !!activeAccount && !!profile && brandingReady;

  return { isReady, profile };
};
